/* eslint-disable no-nested-ternary */
import {
  InfiniteData,
  UseMutationResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { APIError, DefaultQueryParams } from '@data';
import { criteriaActivityKeys, trialCriteriaKeys } from '@data/constants';
import {
  CriteriaFeedbackBody,
  CriteriaUpdateBody,
  ListCriteriaActivityView,
  ListTrialCriteriaView,
  TrialCriteriaView,
} from '@data/sdk/match';
import { MatchingApi } from '@data/utils/axios-instance';

export const useEditCriteria = (): UseMutationResult<
  unknown,
  APIError<CriteriaUpdateBody>,
  { criteriaId: string; values: CriteriaUpdateBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ criteriaId, values }) =>
      MatchingApi.criteria.updateCriteria({ id: criteriaId, criteriaUpdateBody: values }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: trialCriteriaKeys.all() }),
  });
};
// TODO: ask business if we need this functionality
export const useAddCriteriaFeedback = (): UseMutationResult<
  unknown,
  APIError<CriteriaFeedbackBody>,
  { criteriaId: string; values: CriteriaFeedbackBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ criteriaId, values }) => {
      const response = await MatchingApi.criteria.createFeedback({
        id: criteriaId,
        criteriaFeedbackBody: values,
      });
      return response.data;
    },
    onSuccess: (_, { criteriaId }) => {
      queryClient.invalidateQueries({ queryKey: criteriaActivityKeys.allForCriteria(criteriaId) });
      queryClient.invalidateQueries({ queryKey: trialCriteriaKeys.all() });
    },
  });
};

export const useCriteriaActivity = (criteriaId: string, params: DefaultQueryParams) =>
  useQuery<ListCriteriaActivityView, Error>({
    queryKey: criteriaActivityKeys.list(criteriaId, params),
    queryFn: async () => {
      const response = await MatchingApi.criteria.getActivity({ id: criteriaId, ...params });
      return response.data;
    },
    enabled: !!criteriaId,
  });

export const useAddCriteriaComment = (): UseMutationResult<
  unknown,
  APIError<{ comment: string }>,
  { comment: string; criteriaId: string },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ criteriaId, comment }) => {
      const response = await MatchingApi.criteria.createComment({
        id: criteriaId,
        criteriaCommentBody: { comment },
      });
      return response.data;
    },
    onSuccess: (_, { criteriaId }) => {
      queryClient.invalidateQueries({ queryKey: criteriaActivityKeys.allForCriteria(criteriaId) });
      queryClient.invalidateQueries({ queryKey: trialCriteriaKeys.all() });
    },
  });
};

export const useDeleteCriteria = (): UseMutationResult<unknown, APIError, string, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (criteriaId) => {
      const response = await MatchingApi.criteria.deleteCriteria({ id: criteriaId });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trialCriteriaKeys.all() });
    },
  });
};

export const useRestoreCriteria = (): UseMutationResult<unknown, APIError, string, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (criteriaId) => {
      const response = await MatchingApi.criteria.restoreCriteria({ id: criteriaId });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trialCriteriaKeys.all() });
    },
  });
};

export const useChangeCriteriaPosition = (): UseMutationResult<
  unknown,
  APIError,
  {
    criteriaId: string;
    newCriteriaList: TrialCriteriaView[];
    position: number;
    queryParams: DefaultQueryParams;
    trialId: string;
  },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ criteriaId, position }) => {
      const response = await MatchingApi.criteria.moveCriteria({
        id: criteriaId,
        criteriaMoveBody: { newPosition: position },
      });
      return response.data;
    },
    onMutate: async ({ trialId, newCriteriaList, queryParams }) => {
      await queryClient.cancelQueries({ queryKey: trialCriteriaKeys.all() });

      const previousCriteriaList = queryClient.getQueryData<InfiniteData<ListTrialCriteriaView>>(
        trialCriteriaKeys.list(trialId, queryParams),
      );

      if (previousCriteriaList) {
        const newPages = previousCriteriaList.pages.map(
          (page: { items: TrialCriteriaView[] }, index) => {
            const pageOffset =
              (previousCriteriaList.pageParams[index] as DefaultQueryParams)?.offset ?? 0;
            const limit =
              (previousCriteriaList.pageParams[0] as DefaultQueryParams)?.limit ??
              page.items?.length;

            return {
              ...page,
              items: newCriteriaList.slice(pageOffset, limit + pageOffset),
            };
          },
        );

        queryClient.setQueryData(trialCriteriaKeys.list(trialId, queryParams), {
          ...previousCriteriaList,
          pages: newPages,
        });
      }

      return { previousCriteriaList };
    },
    onError: (_, { trialId, queryParams }, context) =>
      queryClient.setQueryData(
        trialCriteriaKeys.list(trialId, queryParams),
        context?.previousCriteriaList,
      ),
  });
};
